import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { DashboardShell } from '../../components/DashboardShell.js';
import { api, ApiClientError } from '../../lib/api.js';
import { useCurrentOrg } from '../../lib/orgs.js';
import {
  useEventByChannel,
  useEventOrders,
  useEventSummary,
  type OrderListItem,
  type OrderStatus,
} from '../../lib/sales.js';
import { OnboardingPage } from './OnboardingPage.js';

type Locale = 'es' | 'en';

interface EventLite {
  id: string;
  slug: string;
  status: 'draft' | 'live' | 'ended' | 'cancelled';
  currency: string;
  defaultLocale: Locale;
  translations: Record<string, { title: string; description?: string; venueLabel?: string }>;
}

export function EventSalesPage() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { lang, id } = useParams();
  const org = useCurrentOrg();

  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Debounce simple para no pegarle al endpoint en cada tecla.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQ(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  const eventQuery = useQuery<EventLite>({
    queryKey: ['event', id, 'lite'],
    queryFn: () => api<EventLite>(`/events/${id}`),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });

  const summary = useEventSummary(id ?? null);
  const channels = useEventByChannel(id ?? null);
  const orders = useEventOrders(id ?? null, {
    status: statusFilter === 'all' ? undefined : statusFilter,
    q: debouncedQ || undefined,
    page,
    pageSize,
  });

  if (!org) return <OnboardingPage />;

  const event = eventQuery.data;
  const eventTitle =
    event &&
    (event.translations[i18n.language]?.title ??
      event.translations[event.defaultLocale]?.title ??
      event.slug);

  const currency = event?.currency ?? summary.data?.currency ?? 'USD';
  const fmt = new Intl.NumberFormat(i18n.language, { style: 'currency', currency });
  const fmtNum = new Intl.NumberFormat(i18n.language);
  const fmtDate = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (eventQuery.isLoading || !event) {
    return (
      <DashboardShell>
        <div className="text-neutral-500 text-sm">{t('event_sales.loading')}</div>
      </DashboardShell>
    );
  }
  if (eventQuery.isError) {
    return (
      <DashboardShell>
        <div className="card text-center text-neutral-400">
          {(eventQuery.error as ApiClientError).message}
        </div>
      </DashboardShell>
    );
  }

  const revenueCents = orders.data?.summary.revenueCents ?? summary.data?.revenueCents ?? 0;
  const feesCents = orders.data?.summary.feesCents ?? 0;
  const ordersCount = orders.data?.summary.ordersCount ?? summary.data?.ordersCount ?? 0;
  const ticketsSold = orders.data?.summary.ticketsSold ?? summary.data?.ticketsSold ?? 0;
  const qrsByStatus = summary.data?.qrsByStatus ?? {};

  // Comisión de plataforma: el backend devuelve el monto real (summary) y la
  // tarifa configurada (platformFee). Solo mostramos las tarjetas si hay
  // comisión activa, para no llenar de ceros cuando está desactivada.
  const platformFeeCents = orders.data?.summary.platformFeeCents ?? 0;
  const netCents = orders.data?.summary.netCents ?? revenueCents;
  const feePct = orders.data?.platformFee.pct ?? 0;
  const feeFixedCents = orders.data?.platformFee.fixedCents ?? 0;
  const platformFeeEnabled = feePct > 0 || feeFixedCents > 0;
  const feeRateLabel = [
    feePct > 0 ? `${feePct}%` : null,
    feeFixedCents > 0 ? fmt.format(feeFixedCents / 100) : null,
  ]
    .filter(Boolean)
    .join(' + ');

  const filters: Array<{ id: OrderStatus | 'all'; label: string }> = [
    { id: 'all', label: t('sales.filter_all') },
    { id: 'paid', label: t('sales.filter_paid') },
    { id: 'fulfilled', label: t('sales.filter_fulfilled') },
    { id: 'refunded', label: t('sales.filter_refunded') },
    { id: 'failed', label: t('sales.filter_failed') },
  ];

  return (
    <DashboardShell>
      <div className="mb-6">
        <Link
          to={`/${lang}/dashboard/events/${event.id}`}
          className="text-sm text-neutral-500 hover:text-neutral-300"
        >
          {t('sales.back_to_event')}
        </Link>
        <h1 className="text-3xl font-bold mt-1 break-words">{t('sales.title')}</h1>
        <p className="text-neutral-400 mt-1 text-sm break-words">
          {t('sales.subtitle', { eventTitle: eventTitle ?? '' })}
        </p>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <Stat
          label={t('sales.revenue_label')}
          value={fmt.format(revenueCents / 100)}
          sub={t('sales.revenue_help')}
        />
        <Stat
          label={t('sales.fees_label')}
          value={fmt.format(feesCents / 100)}
          sub={t('sales.fees_help')}
        />
        {platformFeeEnabled && (
          <>
            <Stat
              label={t('sales.platform_fee_label')}
              value={`− ${fmt.format(platformFeeCents / 100)}`}
              sub={t('sales.platform_fee_help', { rate: feeRateLabel })}
            />
            <Stat
              label={t('sales.net_label')}
              value={fmt.format(netCents / 100)}
              sub={t('sales.net_help')}
            />
          </>
        )}
        <Stat label={t('sales.orders_label')} value={fmtNum.format(ordersCount)} />
        <Stat label={t('sales.tickets_label')} value={fmtNum.format(ticketsSold)} />
      </section>

      <section className="card mb-8">
        <h2 className="font-semibold mb-3">{t('sales.qrs_status_title')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {(['issued', 'validated', 'voided', 'refunded'] as const).map((k) => (
            <div key={k} className="rounded-xl border border-neutral-800 bg-neutral-950/40 p-3">
              <p className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t(`sales.qr_status.${k}`)}
              </p>
              <p className="text-2xl font-bold mt-1 tabular-nums">
                {fmtNum.format(qrsByStatus[k] ?? 0)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="font-semibold mb-3">{t('sales.channel_title')}</h2>
        {channels.isLoading ? (
          <p className="text-neutral-500 text-sm">{t('event_sales.loading')}</p>
        ) : channels.data && channels.data.breakdown.length > 0 ? (
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">{t('sales.channel_source')}</th>
                  <th className="text-left px-4 py-3">{t('sales.channel_medium')}</th>
                  <th className="text-right px-4 py-3">{t('sales.channel_revenue')}</th>
                  <th className="text-right px-4 py-3">{t('sales.channel_orders')}</th>
                </tr>
              </thead>
              <tbody>
                {channels.data.breakdown.map((row, idx) => (
                  <tr
                    key={`${row.source}-${row.medium ?? ''}-${idx}`}
                    className="border-t border-neutral-900"
                  >
                    <td className="px-4 py-3 text-neutral-200">{row.source || '—'}</td>
                    <td className="px-4 py-3 text-neutral-400">{row.medium ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-neutral-200 tabular-nums">
                      {fmt.format(row.revenueCents / 100)}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-400 tabular-nums">
                      {fmtNum.format(row.ordersCount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card text-center text-neutral-400 text-sm">
            {t('sales.channel_empty')}
          </div>
        )}
      </section>

      <section>
        <div className="flex flex-col gap-3 mb-4">
          <h2 className="font-semibold">{t('sales.orders_title')}</h2>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('sales.search_placeholder')}
            className="input"
          />
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => {
              const active = statusFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setStatusFilter(f.id);
                    setPage(1);
                  }}
                  className={[
                    'text-xs px-3 py-1.5 rounded-full border transition',
                    active
                      ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] border-[var(--color-accent)]'
                      : 'border-neutral-800 text-neutral-300 hover:border-neutral-600',
                  ].join(' ')}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {orders.isLoading ? (
          <p className="text-neutral-500 text-sm">{t('event_sales.loading')}</p>
        ) : orders.data && orders.data.orders.length > 0 ? (
          <>
            <ul className="space-y-3 sm:hidden">
              {orders.data.orders.map((o) => (
                <OrderCard key={o.id} order={o} fmt={fmt} fmtDate={fmtDate} fmtNum={fmtNum} />
              ))}
            </ul>
            <div className="hidden sm:block card overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">{t('sales.buyer_column')}</th>
                    <th className="text-left px-4 py-3">{t('sales.items_column')}</th>
                    <th className="text-left px-4 py-3">{t('sales.channel_column')}</th>
                    <th className="text-right px-4 py-3">{t('sales.total_column')}</th>
                    <th className="text-right px-4 py-3">{t('sales.date_column')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.data.orders.map((o) => (
                    <OrderRow key={o.id} order={o} fmt={fmt} fmtDate={fmtDate} fmtNum={fmtNum} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <p className="text-xs text-neutral-500">
                {t('sales.page_summary', {
                  shown: orders.data.orders.length + (page - 1) * pageSize,
                  total: orders.data.total,
                })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="btn-secondary text-sm flex-1 sm:flex-none disabled:opacity-50"
                >
                  {tCommon('actions.previous')}
                </button>
                <button
                  type="button"
                  disabled={!orders.data.hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  className="btn-secondary text-sm flex-1 sm:flex-none disabled:opacity-50"
                >
                  {t('sales.load_more')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="card text-center text-neutral-400 text-sm">{t('sales.orders_empty')}</div>
        )}
      </section>
    </DashboardShell>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="text-2xl sm:text-3xl font-bold mt-2 break-all tabular-nums">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{sub}</p>}
    </div>
  );
}

function OrderRow({
  order,
  fmt,
  fmtDate,
  fmtNum,
}: {
  order: OrderListItem;
  fmt: Intl.NumberFormat;
  fmtDate: Intl.DateTimeFormat;
  fmtNum: Intl.NumberFormat;
}) {
  return (
    <tr className="border-t border-neutral-900">
      <td className="px-4 py-3 align-top">
        <p className="text-neutral-100 font-medium truncate max-w-[200px]">
          {order.buyer.name || order.buyer.email}
        </p>
        <p className="text-xs text-neutral-500 truncate max-w-[200px]">{order.buyer.email}</p>
        <StatusPill status={order.status} />
      </td>
      <td className="px-4 py-3 align-top text-neutral-300">
        {order.items.map((it, i) => (
          <p key={i} className="text-xs">
            {fmtNum.format(it.quantity)} × {it.ticketSnapshot.name}
          </p>
        ))}
      </td>
      <td className="px-4 py-3 align-top text-neutral-400 text-xs">
        {order.utm.source ?? '—'}
        {order.utm.medium ? ` · ${order.utm.medium}` : ''}
      </td>
      <td className="px-4 py-3 align-top text-right text-neutral-100 font-semibold tabular-nums">
        {fmt.format(order.totalCents / 100)}
      </td>
      <td className="px-4 py-3 align-top text-right text-neutral-400 text-xs whitespace-nowrap">
        {fmtDate.format(new Date(order.createdAt))}
      </td>
    </tr>
  );
}

function OrderCard({
  order,
  fmt,
  fmtDate,
  fmtNum,
}: {
  order: OrderListItem;
  fmt: Intl.NumberFormat;
  fmtDate: Intl.DateTimeFormat;
  fmtNum: Intl.NumberFormat;
}) {
  return (
    <li className="card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <p className="text-neutral-100 font-medium truncate">
            {order.buyer.name || order.buyer.email}
          </p>
          <p className="text-xs text-neutral-500 truncate">{order.buyer.email}</p>
          <StatusPill status={order.status} />
        </div>
        <p className="text-lg font-bold tabular-nums whitespace-nowrap">
          {fmt.format(order.totalCents / 100)}
        </p>
      </div>
      <div className="text-xs text-neutral-300 space-y-0.5 mb-3">
        {order.items.map((it, i) => (
          <p key={i}>
            {fmtNum.format(it.quantity)} × {it.ticketSnapshot.name}
          </p>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>{order.utm.source ?? '—'}</span>
        <span>{fmtDate.format(new Date(order.createdAt))}</span>
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: OrderStatus }) {
  const { t } = useTranslation('dashboard');
  const map: Record<OrderStatus, string> = {
    pending: 'bg-neutral-700/40 text-neutral-300 border-neutral-700',
    paid: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    fulfilled: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    refunded: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    failed: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  return (
    <span
      className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[status]}`}
    >
      {t(`sales.order_status.${status}`)}
    </span>
  );
}
